import React, { useState, useEffect } from 'react'
import {
  getVendors,
  getVendorMenu,
  createVendor,
  updateVendor,
  deleteVendor,
  createMenuItem,
  adminUpdateMenuItem,
  deleteMenuItem,
  FoodVendor,
  FoodMenuItem
} from '@/api/foodOrders'
import { Store, Plus, Edit, Trash2, X, Save, UtensilsCrossed } from 'lucide-react'
import toast from 'react-hot-toast'
import { getErrorMessage } from '../utils/helpers'

type ViewMode = 'vendors' | 'menu'
type ModalMode = 'none' | 'add-vendor' | 'edit-vendor' | 'add-menu-item' | 'edit-menu-item'

const VENDOR_TYPES = ['restaurant', 'fast_food', 'catering', 'grocery', 'other']
const DAYS_OF_WEEK = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']
const MENU_CATEGORIES = ['Entrees', 'Nuggets', 'Salads', 'Sides', 'Beverages', 'Breakfast', 'Treats', 'Desserts']

interface VendorFormData {
  vendor_name: string
  vendor_type: string
  is_active: boolean
  ordering_days: string[]
  cutoff_time: string
  delivery_time: string
  phone: string
  website: string
  notes: string
}

interface MenuItemFormData {
  item_name: string
  category: string
  description: string
  price: string
  is_available: boolean
  allergens: string
  sort_order: string
}

export function ParentVendorManagement() {
  const [viewMode, setViewMode] = useState<ViewMode>('vendors')
  const [vendors, setVendors] = useState<FoodVendor[]>([])
  const [selectedVendor, setSelectedVendor] = useState<FoodVendor | null>(null)
  const [menuItems, setMenuItems] = useState<FoodMenuItem[]>([])
  const [loading, setLoading] = useState(false)
  const [modalMode, setModalMode] = useState<ModalMode>('none')
  const [editingVendor, setEditingVendor] = useState<FoodVendor | null>(null)
  const [editingMenuItem, setEditingMenuItem] = useState<FoodMenuItem | null>(null)

  const [vendorForm, setVendorForm] = useState<VendorFormData>({
    vendor_name: '',
    vendor_type: 'restaurant',
    is_active: true,
    ordering_days: [],
    cutoff_time: '10:00',
    delivery_time: '12:00',
    phone: '',
    website: '',
    notes: ''
  })

  const [menuItemForm, setMenuItemForm] = useState<MenuItemFormData>({
    item_name: '',
    category: 'Entrees',
    description: '',
    price: '',
    is_available: true,
    allergens: '',
    sort_order: '0'
  })

  useEffect(() => {
    loadVendors()
  }, [])

  useEffect(() => {
    if (selectedVendor) {
      loadMenuItems(selectedVendor.id)
    }
  }, [selectedVendor])

  const loadVendors = async () => {
    try {
      setLoading(true)
      const data = await getVendors(false)
      setVendors(data || [])
    } catch (error) {
      console.error('Failed to load vendors:', error)
      toast.error('Failed to load vendors')
    } finally {
      setLoading(false)
    }
  }

  const loadMenuItems = async (vendorId: string) => {
    try {
      const data = await getVendorMenu(vendorId)
      setMenuItems(data?.menu_items || [])
    } catch (error) {
      console.error('Failed to load menu items:', error)
      toast.error('Failed to load menu items')
    }
  }

  const openAddVendorModal = () => {
    setVendorForm({
      vendor_name: '',
      vendor_type: 'restaurant',
      is_active: true,
      ordering_days: [],
      cutoff_time: '10:00',
      delivery_time: '12:00',
      phone: '',
      website: '',
      notes: ''
    })
    setModalMode('add-vendor')
  }

  const openEditVendorModal = (vendor: FoodVendor) => {
    setEditingVendor(vendor)
    setVendorForm({
      vendor_name: vendor.vendor_name,
      vendor_type: vendor.vendor_type,
      is_active: vendor.is_active,
      ordering_days: vendor.ordering_days || [],
      cutoff_time: vendor.cutoff_time || '10:00',
      delivery_time: vendor.delivery_time || '12:00',
      phone: vendor.phone || '',
      website: vendor.website || '',
      notes: vendor.notes || ''
    })
    setModalMode('edit-vendor')
  }

  const openAddMenuItemModal = () => {
    if (!selectedVendor) return
    setMenuItemForm({
      item_name: '',
      category: 'Entrees',
      description: '',
      price: '',
      is_available: true,
      allergens: '',
      sort_order: '0'
    })
    setModalMode('add-menu-item')
  }

  const openEditMenuItemModal = (item: FoodMenuItem) => {
    setEditingMenuItem(item)
    setMenuItemForm({
      item_name: item.item_name,
      category: item.category,
      description: item.description || '',
      price: item.price.toString(),
      is_available: item.is_available,
      allergens: item.allergens?.join(', ') || '',
      sort_order: item.sort_order?.toString() || '0'
    })
    setModalMode('edit-menu-item')
  }

  const handleSaveVendor = async () => {
    try {
      if (!vendorForm.vendor_name.trim()) {
        toast.error('Vendor name is required')
        return
      }

      const vendorData = {
        vendor_name: vendorForm.vendor_name,
        vendor_type: vendorForm.vendor_type,
        is_active: vendorForm.is_active,
        ordering_days: vendorForm.ordering_days,
        cutoff_time: vendorForm.cutoff_time || null,
        delivery_time: vendorForm.delivery_time || null,
        phone: vendorForm.phone || null,
        website: vendorForm.website || null,
        notes: vendorForm.notes || null
      }

      if (modalMode === 'add-vendor') {
        await createVendor(vendorData)
        toast.success('Vendor created successfully')
      } else if (modalMode === 'edit-vendor' && editingVendor) {
        await updateVendor(editingVendor.id, vendorData)
        toast.success('Vendor updated successfully')
      }

      setModalMode('none')
      setEditingVendor(null)
      await loadVendors()
    } catch (error: any) {
      console.error('Failed to save vendor:', error)
      toast.error(getErrorMessage(error, 'Failed to save vendor'))
    }
  }

  const handleDeleteVendor = async (vendor: FoodVendor) => {
    if (!confirm(`Are you sure you want to delete ${vendor.vendor_name}? This will also delete all menu items for this vendor.`)) {
      return
    }

    try {
      await deleteVendor(vendor.id)
      toast.success('Vendor deleted successfully')
      if (selectedVendor?.id === vendor.id) {
        setSelectedVendor(null)
        setViewMode('vendors')
      }
      await loadVendors()
    } catch (error: any) {
      console.error('Failed to delete vendor:', error)
      toast.error(getErrorMessage(error, 'Failed to delete vendor'))
    }
  }

  const handleSaveMenuItem = async () => {
    if (!selectedVendor) return

    try {
      if (!menuItemForm.item_name.trim()) {
        toast.error('Item name is required')
        return
      }

      if (!menuItemForm.price || parseFloat(menuItemForm.price) <= 0) {
        toast.error('Valid price is required')
        return
      }

      const menuItemData = {
        item_name: menuItemForm.item_name,
        category: menuItemForm.category,
        description: menuItemForm.description || null,
        price: parseFloat(menuItemForm.price),
        is_available: menuItemForm.is_available,
        allergens: menuItemForm.allergens ? menuItemForm.allergens.split(',').map(a => a.trim()).filter(Boolean) : [],
        sort_order: parseInt(menuItemForm.sort_order) || 0
      }

      if (modalMode === 'add-menu-item') {
        await createMenuItem(selectedVendor.id, menuItemData)
        toast.success('Menu item created successfully')
      } else if (modalMode === 'edit-menu-item' && editingMenuItem) {
        await adminUpdateMenuItem(editingMenuItem.id, menuItemData)
        toast.success('Menu item updated successfully')
      }

      setModalMode('none')
      setEditingMenuItem(null)
      await loadMenuItems(selectedVendor.id)
    } catch (error: any) {
      console.error('Failed to save menu item:', error)
      toast.error(getErrorMessage(error, 'Failed to save menu item'))
    }
  }

  const handleDeleteMenuItem = async (item: FoodMenuItem) => {
    if (!confirm(`Are you sure you want to delete ${item.item_name}?`)) {
      return
    }

    try {
      await deleteMenuItem(item.id)
      toast.success('Menu item deleted successfully')
      if (selectedVendor) {
        await loadMenuItems(selectedVendor.id)
      }
    } catch (error: any) {
      console.error('Failed to delete menu item:', error)
      toast.error(getErrorMessage(error, 'Failed to delete menu item'))
    }
  }

  const toggleDay = (day: string) => {
    setVendorForm(prev => ({
      ...prev,
      ordering_days: prev.ordering_days.includes(day)
        ? prev.ordering_days.filter(d => d !== day)
        : [...prev.ordering_days, day]
    }))
  }

  const renderVendorsList = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">Food Vendors</h2>
        <button
          onClick={openAddVendorModal}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700"
        >
          <Plus className="w-5 h-5" />
          Add Vendor
        </button>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
        </div>
      ) : vendors.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
          <Store className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">No vendors yet. Add your first vendor to get started.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {vendors.map(vendor => (
            <div key={vendor.id} className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <h3 className="font-semibold text-lg text-gray-900">{vendor.vendor_name}</h3>
                  <p className="text-sm text-gray-600 capitalize">{vendor.vendor_type.replace('_', ' ')}</p>
                  <span className={`inline-block mt-2 px-2 py-1 text-xs rounded-full ${vendor.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                    {vendor.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>

              {vendor.phone && <p className="text-sm text-gray-600 mb-1">Phone: {vendor.phone}</p>}
              {vendor.website && <p className="text-sm text-gray-600 mb-1 truncate">Website: {vendor.website}</p>}
              {vendor.ordering_days && vendor.ordering_days.length > 0 && (
                <p className="text-sm text-gray-600 mb-3">Days: {vendor.ordering_days.slice(0, 3).join(', ')}{vendor.ordering_days.length > 3 && '...'}</p>
              )}

              <div className="flex gap-2 mt-4">
                <button
                  onClick={() => {
                    setSelectedVendor(vendor)
                    setViewMode('menu')
                  }}
                  className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-blue-50 text-blue-600 font-medium rounded-lg hover:bg-blue-100"
                >
                  <UtensilsCrossed className="w-4 h-4" />
                  Menu
                </button>
                <button
                  onClick={() => openEditVendorModal(vendor)}
                  className="flex items-center justify-center gap-2 px-3 py-2 bg-gray-100 text-gray-700 font-medium rounded-lg hover:bg-gray-200"
                >
                  <Edit className="w-4 h-4" />
                </button>
                <button
                  onClick={() => handleDeleteVendor(vendor)}
                  className="flex items-center justify-center gap-2 px-3 py-2 bg-red-50 text-red-600 font-medium rounded-lg hover:bg-red-100"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )

  const renderMenuItems = () => {
    if (!selectedVendor) return null

    const categorizedItems = menuItems.reduce((acc, item) => {
      if (!acc[item.category]) acc[item.category] = []
      acc[item.category].push(item)
      return acc
    }, {} as Record<string, FoodMenuItem[]>)

    return (
      <div className="space-y-4">
        <div className="flex items-center gap-4">
          <button
            onClick={() => setViewMode('vendors')}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Vendors
          </button>
          <div className="flex-1">
            <h2 className="text-2xl font-bold text-gray-900">{selectedVendor.vendor_name} Menu</h2>
          </div>
          <button
            onClick={openAddMenuItemModal}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700"
          >
            <Plus className="w-5 h-5" />
            Add Menu Item
          </button>
        </div>

        {menuItems.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
            <UtensilsCrossed className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">No menu items yet. Add your first menu item to get started.</p>
          </div>
        ) : (
          <div className="space-y-6">
            {Object.entries(categorizedItems).map(([category, items]) => (
              <div key={category}>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">{category}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {items.map(item => (
                    <div key={item.id} className="bg-white border border-gray-200 rounded-lg p-4">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1">
                          <h4 className="font-semibold text-gray-900">{item.item_name}</h4>
                          <p className="text-lg font-bold text-blue-600">${item.price.toFixed(2)}</p>
                        </div>
                        <span className={`px-2 py-1 text-xs rounded-full ${item.is_available ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                          {item.is_available ? 'Available' : 'Unavailable'}
                        </span>
                      </div>
                      {item.description && <p className="text-sm text-gray-600 mb-2">{item.description}</p>}
                      {item.allergens && item.allergens.length > 0 && (
                        <p className="text-xs text-gray-500 mb-2">Allergens: {item.allergens.join(', ')}</p>
                      )}
                      <div className="flex gap-2 mt-3">
                        <button
                          onClick={() => openEditMenuItemModal(item)}
                          className="flex-1 flex items-center justify-center gap-2 px-3 py-1.5 bg-gray-100 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-200"
                        >
                          <Edit className="w-4 h-4" />
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeleteMenuItem(item)}
                          className="flex items-center justify-center gap-2 px-3 py-1.5 bg-red-50 text-red-600 text-sm font-medium rounded-lg hover:bg-red-100"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    )
  }

  const renderVendorModal = () => {
    if (modalMode !== 'add-vendor' && modalMode !== 'edit-vendor') return null

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">
              {modalMode === 'add-vendor' ? 'Add New Vendor' : 'Edit Vendor'}
            </h3>
            <button onClick={() => setModalMode('none')} className="text-gray-500 hover:text-gray-700">
              <X className="w-6 h-6" />
            </button>
          </div>

          <div className="p-6 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Vendor Name *</label>
              <input
                type="text"
                value={vendorForm.vendor_name}
                onChange={e => setVendorForm(prev => ({ ...prev, vendor_name: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="e.g., Chick-fil-A"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Vendor Type *</label>
              <select
                value={vendorForm.vendor_type}
                onChange={e => setVendorForm(prev => ({ ...prev, vendor_type: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                {VENDOR_TYPES.map(type => (
                  <option key={type} value={type}>{type.replace('_', ' ').charAt(0).toUpperCase() + type.replace('_', ' ').slice(1)}</option>
                ))}
              </select>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                checked={vendorForm.is_active}
                onChange={e => setVendorForm(prev => ({ ...prev, is_active: e.target.checked }))}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <label className="ml-2 text-sm font-medium text-gray-700">Active</label>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Ordering Days</label>
              <div className="grid grid-cols-2 gap-2">
                {DAYS_OF_WEEK.map(day => (
                  <button
                    key={day}
                    onClick={() => toggleDay(day)}
                    className={`px-3 py-2 text-sm font-medium rounded-lg border ${
                      vendorForm.ordering_days.includes(day)
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {day}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Cutoff Time</label>
                <input
                  type="time"
                  value={vendorForm.cutoff_time}
                  onChange={e => setVendorForm(prev => ({ ...prev, cutoff_time: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Delivery Time</label>
                <input
                  type="time"
                  value={vendorForm.delivery_time}
                  onChange={e => setVendorForm(prev => ({ ...prev, delivery_time: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
              <input
                type="tel"
                value={vendorForm.phone}
                onChange={e => setVendorForm(prev => ({ ...prev, phone: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="(555) 123-4567"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Website</label>
              <input
                type="url"
                value={vendorForm.website}
                onChange={e => setVendorForm(prev => ({ ...prev, website: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="https://example.com"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
              <textarea
                value={vendorForm.notes}
                onChange={e => setVendorForm(prev => ({ ...prev, notes: e.target.value }))}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="e.g., Closed on Sundays"
              />
            </div>
          </div>

          <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 p-4 flex gap-3">
            <button
              onClick={() => setModalMode('none')}
              className="flex-1 px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 font-medium"
            >
              Cancel
            </button>
            <button
              onClick={handleSaveVendor}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
            >
              <Save className="w-5 h-5" />
              Save Vendor
            </button>
          </div>
        </div>
      </div>
    )
  }

  const renderMenuItemModal = () => {
    if (modalMode !== 'add-menu-item' && modalMode !== 'edit-menu-item') return null

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">
              {modalMode === 'add-menu-item' ? 'Add New Menu Item' : 'Edit Menu Item'}
            </h3>
            <button onClick={() => setModalMode('none')} className="text-gray-500 hover:text-gray-700">
              <X className="w-6 h-6" />
            </button>
          </div>

          <div className="p-6 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Item Name *</label>
              <input
                type="text"
                value={menuItemForm.item_name}
                onChange={e => setMenuItemForm(prev => ({ ...prev, item_name: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="e.g., Spicy Deluxe Sandwich"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Category *</label>
              <select
                value={menuItemForm.category}
                onChange={e => setMenuItemForm(prev => ({ ...prev, category: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                {MENU_CATEGORIES.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Price *</label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={menuItemForm.price}
                onChange={e => setMenuItemForm(prev => ({ ...prev, price: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="0.00"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <textarea
                value={menuItemForm.description}
                onChange={e => setMenuItemForm(prev => ({ ...prev, description: e.target.value }))}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Item description"
              />
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                checked={menuItemForm.is_available}
                onChange={e => setMenuItemForm(prev => ({ ...prev, is_available: e.target.checked }))}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <label className="ml-2 text-sm font-medium text-gray-700">Available for ordering</label>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Allergens (comma-separated)</label>
              <input
                type="text"
                value={menuItemForm.allergens}
                onChange={e => setMenuItemForm(prev => ({ ...prev, allergens: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="e.g., Wheat, Soy, Milk"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Sort Order</label>
              <input
                type="number"
                value={menuItemForm.sort_order}
                onChange={e => setMenuItemForm(prev => ({ ...prev, sort_order: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="0"
              />
            </div>
          </div>

          <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 p-4 flex gap-3">
            <button
              onClick={() => setModalMode('none')}
              className="flex-1 px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 font-medium"
            >
              Cancel
            </button>
            <button
              onClick={handleSaveMenuItem}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
            >
              <Save className="w-5 h-5" />
              Save Menu Item
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {viewMode === 'vendors' ? renderVendorsList() : renderMenuItems()}
      {renderVendorModal()}
      {renderMenuItemModal()}
    </div>
  )
}
